import { Injectable, inject, signal } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom, map } from 'rxjs'
import { environment } from '../../environments/environment'
import { ApiResponse } from '../shared/interfaces/api.interface'
import {
  Appointment, ObserverReport, OfficialAccount, RefereeMe, SciSportsSync, SelfReviewItem, SheetResult, SheetRow,
} from './referee-accounts.models'
import { MatchRefereeing } from '../match/refereeing/refereeing.models'

/** Calls for referee & observer accounts and the referees department (CC, 20/10/2026). */
@Injectable({ providedIn: 'root' })
export class RefereeAccountsService {
  private readonly http = inject(HttpClient)
  private readonly api = environment.apiUrl

  private get<T> (url: string): Promise<T> {
    return firstValueFrom(this.http.get<ApiResponse<T>>(url).pipe(map(r => r.data)))
  }
  private send<T> (method: 'put' | 'post' | 'delete', url: string, body?: unknown): Promise<T> {
    const req = method === 'delete'
      ? this.http.delete<ApiResponse<T>>(url)
      : method === 'put'
        ? this.http.put<ApiResponse<T>>(url, body ?? {})
        : this.http.post<ApiResponse<T>>(url, body ?? {})
    return firstValueFrom(req.pipe(map((r: ApiResponse<T>) => r.data)))
  }

  me (): Promise<RefereeMe> { return this.get(`${this.api}/me/referee`) }

  /** Whether this login is named observer on any match: the launcher shows My appointments only then. */
  readonly observes = signal(false)
  /** Observes and never referees: his "page" is the appointments list, so one tile, not two. */
  readonly observesOnly = signal(false)
  private observesFor = ''
  async loadObserves (userId: string): Promise<void> {
    if (this.observesFor === userId) return
    this.observesFor = userId
    try {
      const me = await this.me()
      this.observes.set(me.as_observer > 0)
      this.observesOnly.set(me.as_observer > 0 && me.as_referee + me.as_other_official === 0)
    } catch {
      this.observes.set(false) // not linked yet, or offline: nothing to observe
      this.observesOnly.set(false)
    }
  }
  appointments (): Promise<Appointment[]> { return this.get(`${this.api}/me/appointments`) }

  refereeing (fixtureId: string): Promise<MatchRefereeing> { return this.get(`${this.api}/matches/${fixtureId}/refereeing`) }

  selfReview (fixtureId: string): Promise<SelfReviewItem[]> { return this.get(`${this.api}/matches/${fixtureId}/self-review`) }
  saveSelfReview (fixtureId: string, items: SelfReviewItem[]): Promise<SelfReviewItem[]> {
    return this.send('put', `${this.api}/matches/${fixtureId}/self-review`, { items })
  }

  report (fixtureId: string): Promise<ObserverReport> { return this.get(`${this.api}/matches/${fixtureId}/observer-report`) }
  saveReport (fixtureId: string, r: Partial<ObserverReport>): Promise<ObserverReport> {
    const body = {
      difficulty: r.difficulty, mark: r.mark, areas: r.areas, officials_comments: r.officials_comments,
      incidents: r.incidents, improvement: r.improvement,
    }
    return this.send('put', `${this.api}/matches/${fixtureId}/observer-report`, body)
  }
  submitReport (fixtureId: string): Promise<ObserverReport> { return this.send('post', `${this.api}/matches/${fixtureId}/observer-report/submit`) }
  verifyReport (fixtureId: string, body: { mark: number | null; difficulty: string | null; reason: string }): Promise<ObserverReport> {
    return this.send('post', `${this.api}/admin/matches/${fixtureId}/observer-report/verify`, body)
  }
  releaseReport (fixtureId: string): Promise<ObserverReport> { return this.send('post', `${this.api}/admin/matches/${fixtureId}/observer-report/release`) }
  reopenReport (fixtureId: string, reason: string): Promise<ObserverReport> {
    return this.send('post', `${this.api}/admin/matches/${fixtureId}/observer-report/reopen`, { reason })
  }
  departmentReports (status: string): Promise<Appointment[]> {
    return this.get(`${this.api}/admin/observer-reports${status ? '?status=' + encodeURIComponent(status) : ''}`)
  }
  refereeReports (officialId: string): Promise<Appointment[]> { return this.get(`${this.api}/referees/${officialId}/reports`) }

  account (officialId: string): Promise<OfficialAccount> { return this.get(`${this.api}/admin/referees/${officialId}/account`) }
  linkAccount (officialId: string, email: string): Promise<OfficialAccount> {
    return this.send('put', `${this.api}/admin/referees/${officialId}/account`, { email })
  }
  unlinkAccount (officialId: string): Promise<OfficialAccount> { return this.send('delete', `${this.api}/admin/referees/${officialId}/account`) }
  merge (fromId: string, intoId: string): Promise<{ into_id: string }> {
    return this.send('post', `${this.api}/admin/referees/${fromId}/merge`, { into_id: intoId })
  }
  syncSciSports (): Promise<SciSportsSync> { return this.send('post', `${this.api}/admin/referees/sync-scisports`) }
  importSheet (rows: SheetRow[], dryRun: boolean): Promise<SheetResult[]> {
    return this.send('post', `${this.api}/admin/match-officials/import`, { rows, dry_run: dryRun })
  }
}

/** The server's error message for a failed call, else a fallback. */
export function errorText (e: unknown, fallback: string): string {
  const err = e as { error?: { error?: { message?: string } } }
  return err?.error?.error?.message || fallback
}
